import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Eye, Edit3, UserX, UserCheck, Shield, Trash2, Mail } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import FormField from '../ui/FormField';
import PagedUsersTable from './PagedUsersTable';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const UserManagementTab = ({
  users,
  setUsers,
  loading,
  error,
  showAddUser,
  setShowAddUser,
  editingUser,
  setEditingUser,
  formData,
  setFormData,
  handleInputChange,
  handleAddUser,
  handleUpdateUser,
  handleDeleteUser,
  handleDeactivateUser,
  handleActivateUser,
  handleAssignRole,
  isDeleting,
  handleLoginAsUser,
  fetchUsers,
  appSettings
}) => {
  const { isSuperAdmin, isUserManager } = useAuth();

  // trigger child table refresh
  const [refreshSignal, setRefreshSignal] = useState(0);
  const triggerRefresh = () => setRefreshSignal(Date.now());

  // Email modal state
  const [emailModalUser, setEmailModalUser] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Deactivate modal state
  const [deactivateUserTarget, setDeactivateUserTarget] = useState(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [processingDeactivate, setProcessingDeactivate] = useState(false);

  // Assign role modal state
  const [showAssignRole, setShowAssignRole] = useState(false);
  const [assignUserTarget, setAssignUserTarget] = useState(null);
  const [assignRoleValue, setAssignRoleValue] = useState('user');
  const [assignAdminRole, setAssignAdminRole] = useState('user_manager');
  const [assignReason, setAssignReason] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  const openEmailModal = (u) => {
    setEmailModalUser(u);
    setEmailSubject('');
    setEmailBody('');
  };

  const sendEmail = async () => {
    if (!emailModalUser || !emailSubject.trim() || !emailBody.trim()) return;
    try {
      setSendingEmail(true);
      const adminToken = localStorage.getItem('admin_token');
      const resp = await fetch(`${API_BASE_URL}/api/admin/users/${emailModalUser.id}/email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subject: emailSubject, body: emailBody })
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        alert(data.detail || 'Failed to send email');
      } else {
        alert('Email sent');
        setEmailModalUser(null);
        setEmailSubject('');
        setEmailBody('');
      }
    } catch (e) {
      alert('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Card.Header>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Card.Title>User Management</Card.Title>
            {isSuperAdmin() && (
              <Button icon={<Plus size={16} />} onClick={() => { setEditingUser(null); setFormData({ email: '', full_name: '', company_name: '', phone_number: '', password: '' }); setShowAddUser(true); }}>Add User</Button>
            )}
          </div>
        </Card.Header>
        <Card.Content>
          <PagedUsersTable 
            appSettings={appSettings}
            refreshSignal={refreshSignal}
            renderActions={(u) => {
              const superAdmin = isSuperAdmin();
              const userManager = isUserManager();
              return (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {superAdmin && (
                    <>
                      <button onClick={() => handleLoginAsUser(u.id)} style={{ padding: '6px 8px', backgroundColor: '#3b82f6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }} title="Login as user">
                        <Eye size={12} />
                      </button>
                      <button onClick={() => { setEditingUser(u); setFormData({ email: u.email || '', full_name: u.full_name || '', company_name: u.company_name || '', phone_number: u.phone_number || '', password: '' }); setShowAddUser(true); }} style={{ padding: '6px 8px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }} title="Edit user">
                        <Edit3 size={12} />
                      </button>
                    </>
                  )}

                  {/* Resend verification (both superadmin and user-manager), only when not verified */}
                  {!u.is_verified && (
                    <>
                      {(superAdmin || userManager) && (
                        <button
                          onClick={async () => {
                            try {
                              const adminToken = localStorage.getItem('admin_token');
                              const resp = await fetch(`${API_BASE_URL}/api/admin/users/${u.id}/resend-verification`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${adminToken}` }
                              });
                              if (!resp.ok) {
                                const data = await resp.json().catch(() => ({}));
                                alert(data.detail || 'Failed to resend verification');
                              } else {
                                alert('Verification email resent');
                              }
                            } catch (e) {
                              alert('Failed to resend verification');
                            } finally {
                              triggerRefresh();
                            }
                          }}
                          style={{ padding: '6px 8px', backgroundColor: '#64748b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                          title="Resend verification email"
                        >
                          <Mail size={12} />
                        </button>
                      )}

                      {/* Force verify (superadmin only) */}
                      {superAdmin && (
                        <button
                          onClick={async () => {
                            try {
                              const adminToken = localStorage.getItem('admin_token');
                              const resp = await fetch(`${API_BASE_URL}/api/admin/users/${u.id}/force-verify`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${adminToken}` }
                              });
                              if (!resp.ok) {
                                const data = await resp.json().catch(() => ({}));
                                alert(data.detail || 'Failed to verify user');
                              } else {
                                alert('User verified');
                              }
                            } catch (e) {
                              alert('Failed to verify user');
                            } finally {
                              triggerRefresh();
                            }
                          }}
                          style={{ padding: '6px 8px', backgroundColor: '#16a34a', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                          title="Force verify email"
                        >
                          <UserCheck size={12} />
                        </button>
                      )}
                    </>
                  )}

                  {/* Deactivate/Activate (User Manager can do both) */}
                  {u.is_active ? (
                    <button
                      onClick={() => { setDeactivateUserTarget(u); setDeactivateReason(''); }}
                      style={{ padding: '6px 8px', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                      title="Deactivate user"
                    >
                      <UserX size={12} />
                    </button>
                  ) : (
                    (superAdmin || userManager) && (
                      <button
                        onClick={async () => {
                          await handleActivateUser(u.id);
                          if (typeof fetchUsers === 'function') await fetchUsers();
                          triggerRefresh();
                        }}
                        style={{ padding: '6px 8px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                        title="Activate user"
                      >
                        <UserCheck size={12} />
                      </button>
                    )
                  )}

                  {superAdmin && (
                    <>
                      <button
                        onClick={() => { setAssignUserTarget(u); setAssignRoleValue(u.role || 'user'); setAssignAdminRole(u.admin_role || 'user_manager'); setAssignReason(''); setShowAssignRole(true); }}
                        style={{ padding: '6px 8px', backgroundColor: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                        title="Assign role"
                      >
                        <Shield size={12} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); openEmailModal(u); }} style={{ padding: '6px 8px', backgroundColor: '#64748b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }} title="Email user">
                        <Mail size={12} />
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Delete user? This cannot be undone.')) return;
                          const res = await handleDeleteUser(u.id);
                          if (res?.success && typeof fetchUsers === 'function') await fetchUsers();
                        }}
                        style={{ padding: '6px 8px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                        title="Delete user"
                      >
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
              );
            }}
          />
        </Card.Content>
      </Card>

      {/* Add/Edit User Modal */}
      <Modal
        isOpen={!!showAddUser}
        onClose={() => { setShowAddUser(false); setEditingUser(null); }}
        title={editingUser ? 'Edit User' : 'Add User'}
        maxWidth="max-w-lg"
      >
        <div style={{ padding: 16 }}>
          <FormField label="Email" type="email" name="email" value={formData.email || ''} onChange={handleInputChange} required />
          <FormField label="Full Name" type="text" name="full_name" value={formData.full_name || ''} onChange={handleInputChange} />
          <FormField label="Company Name" type="text" name="company_name" value={formData.company_name || ''} onChange={handleInputChange} />
          <FormField label="Phone Number" type="text" name="phone_number" value={formData.phone_number || ''} onChange={handleInputChange} />
          <FormField label={editingUser ? 'New Password (leave blank to keep current)' : 'Password'} type="password" name="password" value={formData.password || ''} onChange={handleInputChange} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
            <Button variant="secondary" onClick={() => { setShowAddUser(false); setEditingUser(null); }}>Cancel</Button>
            <Button
              onClick={async () => {
                if (editingUser) {
                  const res = await handleUpdateUser(editingUser.id, formData);
                  if (res?.success && typeof fetchUsers === 'function') await fetchUsers();
                } else {
                  const res = await handleAddUser(formData);
                  if (res?.success && typeof fetchUsers === 'function') await fetchUsers();
                }
                setShowAddUser(false);
                setEditingUser(null);
              }}
            >
              {editingUser ? 'Save Changes' : 'Create User'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Deactivate Reason Modal */}
      <Modal
        isOpen={!!deactivateUserTarget}
        onClose={() => { setDeactivateUserTarget(null); setDeactivateReason(''); }}
        title={`Deactivate ${deactivateUserTarget?.full_name || deactivateUserTarget?.email || ''}`}
        maxWidth="max-w-md"
      >
        <div style={{ padding: 16 }}>
          <FormField label="Reason (optional)" type="textarea" rows={4} value={deactivateReason} onChange={(e) => setDeactivateReason(e.target.value)} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
            <Button variant="secondary" onClick={() => { setDeactivateUserTarget(null); setDeactivateReason(''); }}>Cancel</Button>
            <Button
              loading={processingDeactivate}
              disabled={processingDeactivate}
              onClick={async () => {
                try {
                  setProcessingDeactivate(true);
                  const res = await handleDeactivateUser(deactivateUserTarget.id, deactivateReason);
                  if (res?.success && typeof fetchUsers === 'function') await fetchUsers();
                  setDeactivateUserTarget(null);
                  setDeactivateReason('');
                  triggerRefresh();
                } finally {
                  setProcessingDeactivate(false);
                }
              }}
            >
              Confirm Deactivate
            </Button>
          </div>
        </div>
      </Modal>

      {/* Assign Role Modal (Superadmin only) */}
      <Modal
        isOpen={showAssignRole}
        onClose={() => { setShowAssignRole(false); setAssignUserTarget(null); }}
        title={`Assign Role${assignUserTarget ? `: ${assignUserTarget.full_name || assignUserTarget.email}` : ''}`}
        maxWidth="max-w-md"
      >
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="radio" checked={assignRoleValue === 'user'} onChange={() => setAssignRoleValue('user')} /> User
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="radio" checked={assignRoleValue === 'admin'} onChange={() => setAssignRoleValue('admin')} /> Admin
            </label>
          </div>
          {assignRoleValue === 'admin' && (
            <FormField
              label="Admin Role"
              type="select"
              value={assignAdminRole}
              onChange={(e) => setAssignAdminRole(e.target.value)}
              options={[{ value: 'user_manager', label: 'User Manager' }, { value: 'partner_admin', label: 'Partner Admin' }]}
            />
          )}
          <FormField label="Reason (optional)" type="textarea" rows={3} value={assignReason} onChange={(e) => setAssignReason(e.target.value)} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
            <Button variant="secondary" onClick={() => { setShowAssignRole(false); setAssignUserTarget(null); }}>Cancel</Button>
            <Button
              loading={assignLoading}
              disabled={assignLoading}
              onClick={async () => {
                try {
                  if (!assignUserTarget) return;
                  setAssignLoading(true);
                  const res = await handleAssignRole(assignUserTarget.id, assignRoleValue, assignRoleValue === 'admin' ? assignAdminRole : null, assignReason);
                  if (res?.success && typeof fetchUsers === 'function') await fetchUsers();
                  setShowAssignRole(false);
                  setAssignUserTarget(null);
                  triggerRefresh();
                } finally {
                  setAssignLoading(false);
                }
              }}
            >
              Save Role
            </Button>
          </div>
        </div>
      </Modal>

      {/* Email User Modal */}
      <Modal
        isOpen={!!emailModalUser}
        onClose={() => { setEmailModalUser(null); setEmailSubject(''); setEmailBody(''); }}
        title={emailModalUser ? `Email ${emailModalUser.full_name || emailModalUser.email}` : 'Email User'}
        maxWidth="max-w-lg"
      >
        <div style={{ padding: 16 }}>
          <FormField label="Subject" type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Enter subject" required />
          <FormField label="Body" type="textarea" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} rows={6} placeholder="Type your message..." required />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
            <Button variant="secondary" onClick={() => { setEmailModalUser(null); setEmailSubject(''); setEmailBody(''); }}>Cancel</Button>
            <Button onClick={sendEmail} loading={sendingEmail} disabled={sendingEmail}>{sendingEmail ? 'Sending...' : 'Send Email'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UserManagementTab;